import React, { useRef, useState } from 'react';
import { Box, IconButton, TextField, Tooltip, Typography } from '@mui/material';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import SendIcon from '@mui/icons-material/Send';

import VoiceRecordButton from './VoiceRecordButton';
import { CHATBOT_HE } from '../chatbotStrings.he';

export default function ChatInput({
  input,
  setInput,
  handleKeyPress,
  handleSend,
  onVoiceSend,
  onFileUpload,
  disabled,
  isUploadingFile,
  acceptedFileTypes = [],
  maxFileSizeMB = 10,
  compact = false,
}) {
  const [isVoiceRecording, setIsVoiceRecording] = useState(false);
  const fileInputRef = useRef(null);

  const handleRecordingStateChange = (isRecording) => setIsVoiceRecording(isRecording);

  const openFilePicker = () => {
    if (disabled) return;
    fileInputRef.current?.click();
  };

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    await onFileUpload?.(file);
    e.target.value = '';
  };

  const acceptValue = acceptedFileTypes.map((ext) => `.${ext}`).join(',');

  return (
    <Box sx={{ bgcolor: '#fff', borderTop: '1px solid', borderColor: 'divider' }} dir="rtl">
      <Box sx={{ px: compact ? 1.5 : 2, py: compact ? 1 : 2 }}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row-reverse',
            alignItems: 'center',
            gap: compact ? 1 : 1.5,
          }}
        >
          {!isVoiceRecording && (
            <Box sx={{ flex: 1 }}>
              <TextField
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKeyPress}
                placeholder={compact ? CHATBOT_HE.messagePlaceholderCompact : CHATBOT_HE.messagePlaceholderFull}
                multiline
                minRows={1}
                maxRows={compact ? 3 : 5}
                fullWidth
                size="small"
                inputProps={{ dir: 'auto' }}
              />
            </Box>
          )}

          {!isVoiceRecording && (
            <>
              <input
                ref={fileInputRef}
                type="file"
                style={{ display: 'none' }}
                onChange={handleFileChange}
                accept={acceptValue}
              />
              <Tooltip
                title={
                  <span dir="rtl" style={{ display: 'block', textAlign: 'right' }}>
                    {CHATBOT_HE.tooltipAttach(maxFileSizeMB)}
                  </span>
                }
                slotProps={{ tooltip: { dir: 'rtl', sx: { textAlign: 'right' } } }}
              >
                <span>
                  <IconButton
                    type="button"
                    onClick={openFilePicker}
                    disabled={disabled || isUploadingFile}
                    sx={{
                      width: compact ? 36 : 48,
                      height: compact ? 36 : 48,
                      borderRadius: 2,
                      border: '1px solid',
                      borderColor: 'divider',
                    }}
                  >
                    <AttachFileIcon fontSize={compact ? 'small' : 'medium'} />
                  </IconButton>
                </span>
              </Tooltip>
            </>
          )}

          <VoiceRecordButton
            onVoiceSend={onVoiceSend}
            disabled={disabled}
            onRecordingStateChange={handleRecordingStateChange}
            compact={compact}
          />

          {!isVoiceRecording && (
            <Tooltip
              title={
                <span dir="rtl" style={{ display: 'block', textAlign: 'right' }}>
                  {CHATBOT_HE.tooltipSend}
                </span>
              }
              slotProps={{ tooltip: { dir: 'rtl', sx: { textAlign: 'right' } } }}
            >
              <span>
                <IconButton
                  onClick={handleSend}
                  disabled={!input.trim() || disabled}
                  sx={{
                    width: compact ? 36 : 48,
                    height: compact ? 36 : 48,
                    borderRadius: 2,
                    bgcolor: '#304fff',
                    color: '#fff',
                    '&:hover': { bgcolor: '#1e3ad4' },
                    '&.Mui-disabled': { opacity: 0.4, color: '#fff' },
                  }}
                >
                  <SendIcon fontSize={compact ? 'small' : 'medium'} />
                </IconButton>
              </span>
            </Tooltip>
          )}
        </Box>

        {!isVoiceRecording && !compact && (
          <Typography sx={{ mt: 1.5, fontSize: 12, color: 'text.secondary', textAlign: 'center' }} dir="rtl">
            {isUploadingFile ? CHATBOT_HE.uploading : CHATBOT_HE.hintSend}
          </Typography>
        )}
      </Box>
    </Box>
  );
}

